'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';
import AdDisplay from '@/components/AdDisplay';
import NavBar from '@/components/NavBar';
import NewsComments from '@/components/NewsComments';

interface NewsPost {
  id: string;
  title: string;
  slug: string;
  excerpt?: string;
  content?: string;
  cover_image?: string;
  cover_image_alt?: string;
  category?: string;
  author?: string;
  is_featured?: boolean;
  views?: number;
  created_at?: string;
  gallery_images?: string[];
  ad_placement?: string;
  ad_id?: string;
}

interface NewsPostClientProps {
  initialPost: NewsPost | null;
  slug: string;
}

export default function NewsPostClient({ initialPost, slug }: NewsPostClientProps) {
  const [post, setPost] = useState<NewsPost | null>(initialPost);
  const [loading, setLoading] = useState(!initialPost);
  const [notFound, setNotFound] = useState(!initialPost && !!slug);
  const [lightboxImg, setLightboxImg] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number>(0);
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [relatedPosts, setRelatedPosts] = useState<NewsPost[]>([]);
  const shareRef = useRef<HTMLDivElement>(null);
  const supabase = createClient();

  // Load Twitter widget script when post has embedded tweets
  useEffect(() => {
    const currentPost = initialPost || post;
    if (!currentPost?.content) return;
    const hasTweet = /\[twitter:[0-9]+\]/.test(currentPost.content);
    if (!hasTweet) return;
    if ((window as any).twttr) {
      (window as any).twttr.widgets?.load();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://platform.twitter.com/widgets.js';
    script.async = true;
    script.charset = 'utf-8';
    script.onload = () => {
      (window as any).twttr?.widgets?.load();
    };
    document.body.appendChild(script);
  }, [post, initialPost]);

  useEffect(() => {
    try {
      setIsAdmin(sessionStorage.getItem('adminAuthenticated') === '1');
    } catch {}
  }, []);

  useEffect(() => {
    if (!slug) return;
    if (initialPost) {
      supabase
        .from('news_posts')
        .update({ views: (initialPost.views || 0) + 1 })
        .eq('id', initialPost.id);
      return;
    }
    const fetchPost = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('news_posts')
          .select('*')
          .eq('slug', slug)
          .eq('is_published', true)
          .single();
        if (error || !data) {
          setNotFound(true);
        } else {
          setPost(data);
          await supabase
            .from('news_posts')
            .update({ views: (data.views || 0) + 1 })
            .eq('id', data.id);
        }
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  // Fetch related posts once the current post is known
  useEffect(() => {
    const currentPost = initialPost || post;
    if (!currentPost) return;
    const fetchRelated = async () => {
      let query = supabase
        .from('news_posts')
        .select('id, title, slug, excerpt, cover_image, cover_image_alt, category, author, created_at, views')
        .eq('is_published', true)
        .neq('id', currentPost.id)
        .order('created_at', { ascending: false })
        .limit(4);
      if (currentPost.category) {
        query = query.eq('category', currentPost.category);
      }
      const { data } = await query;
      if (data && data.length > 0) {
        setRelatedPosts(data);
      } else if (currentPost.category) {
        // Fallback: fetch latest posts regardless of category
        const { data: fallback } = await supabase
          .from('news_posts')
          .select('id, title, slug, excerpt, cover_image, cover_image_alt, category, author, created_at, views')
          .eq('is_published', true)
          .neq('id', currentPost.id)
          .order('created_at', { ascending: false })
          .limit(4);
        setRelatedPosts(fallback || []);
      }
    };
    fetchRelated();
  }, [post, initialPost]);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const galleryImages: string[] = Array.isArray(post?.gallery_images) ? post!.gallery_images : [];

  const openLightbox = (idx: number) => {
    setLightboxIndex(idx);
    setLightboxImg(galleryImages[idx]);
  };

  const lightboxPrev = () => {
    const newIdx = (lightboxIndex - 1 + galleryImages.length) % galleryImages.length;
    setLightboxIndex(newIdx);
    setLightboxImg(galleryImages[newIdx]);
  };

  const lightboxNext = () => {
    const newIdx = (lightboxIndex + 1) % galleryImages.length;
    setLightboxIndex(newIdx);
    setLightboxImg(galleryImages[newIdx]);
  };

  const getShareUrl = () => {
    const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || 'https://viddstar.com';
    return `${siteUrl}/news/${slug}`;
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
    }
  };

  const shareLinks = (postTitle: string) => {
    const url = encodeURIComponent(getShareUrl());
    const title = encodeURIComponent(postTitle);
    return {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter: `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
      whatsapp: `https://wa.me/?text=${title}%20${url}`,
    };
  };

  // Render ad based on placement setting
  const renderAd = (position: string) => {
    if (!post?.ad_placement || post.ad_placement !== position) return null;
    return (
      <AdDisplay
        placement="blog-post"
        className="my-6"
      />
    );
  };

  // Parse a paragraph string and render markdown images as <img> elements
  const renderParagraph = (text: string, key: number) => {
    // Check for YouTube embed marker [youtube:VIDEO_ID]
    const youtubeMarker = text.trim().match(/^\[youtube:([a-zA-Z0-9_-]{11})\]$/);
    if (youtubeMarker) {
      const videoId = youtubeMarker[1];
      return (
        <div key={key} className="my-6 w-full">
          <div className="relative w-full rounded-[4px] overflow-hidden bg-black" style={{ paddingBottom: '56.25%' }}>
            <iframe
              src={`https://www.youtube.com/embed/${videoId}`}
              title="YouTube video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full border-0"
            />
          </div>
        </div>
      );
    }

    // Check for Twitter/X embed marker [twitter:TWEET_ID]
    const twitterMarker = text.trim().match(/^\[twitter:(\d+)\]$/);
    if (twitterMarker) {
      const tweetId = twitterMarker[1];
      return (
        <div key={key} className="my-6 w-full flex justify-center">
          <blockquote
            className="twitter-tweet"
            data-dnt="true"
            data-theme="dark"
          >
            <a href={`https://twitter.com/i/web/status/${tweetId}`}>Loading tweet…</a>
          </blockquote>
        </div>
      );
    }

    // Check for Instagram embed marker [instagram:SHORTCODE]
    const instagramMarker = text.trim().match(/^\[instagram:([A-Za-z0-9_-]+)\]$/);
    if (instagramMarker) {
      const shortcode = instagramMarker[1];
      return (
        <div key={key} className="my-6 w-full flex justify-center">
          <iframe
            src={`https://www.instagram.com/p/${shortcode}/embed/`}
            className="rounded-[4px] border-0"
            style={{ width: '100%', maxWidth: '540px', minHeight: '600px' }}
            scrolling="no"
            allowTransparency={true}
            allow="encrypted-media"
            title="Instagram post"
          />
        </div>
      );
    }

    // Check for bare YouTube URLs on their own line
    const youtubeUrlPatterns = [
      /^https?:\/\/(?:www\.)?youtube\.com\/watch\?(?:.*&)?v=([a-zA-Z0-9_-]{11})/,
      /^https?:\/\/youtu\.be\/([a-zA-Z0-9_-]{11})/,
      /^https?:\/\/(?:www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
      /^https?:\/\/(?:www\.)?youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
    ];
    for (const pattern of youtubeUrlPatterns) {
      let match = text.trim().match(pattern);
      if (match) {
        const videoId = match[1];
        return (
          <div key={key} className="my-6 w-full">
            <div className="relative w-full rounded-[4px] overflow-hidden bg-black" style={{ paddingBottom: '56.25%' }}>
              <iframe
                src={`https://www.youtube.com/embed/${videoId}`}
                title="YouTube video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full border-0"
              />
            </div>
          </div>
        );
      }
    }

    // Combined regex: markdown images ![alt](url) and markdown links [text](url)
    const tokenRegex = /(!?\[([^\]]*)\]\(([^)]+)\))/g;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    let hasImage = false;

    while ((match = tokenRegex.exec(text)) !== null) {
      const full = match[1];
      const isImage = full.startsWith('!');
      const label = match[2];
      const url = match[3];

      // Text before this token
      if (match.index > lastIndex) {
        parts.push(
          <span key={`text-${lastIndex}`}>{text.slice(lastIndex, match.index)}</span>
        );
      }

      if (isImage) {
        hasImage = true;
        parts.push(
          <img
            key={`img-${match.index}`}
            src={url}
            alt={label || ''}
            className="w-full h-auto rounded-[4px] my-4 block max-h-[300px] sm:max-h-[450px] md:max-h-[600px] object-contain mx-auto"
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/assets/images/no_image.png'; }}
          />
        );
      } else {
        // Markdown link
        parts.push(
          <a
            key={`link-${match.index}`}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-red underline hover:text-red-400 transition-colors break-all"
          >
            {label || url}
          </a>
        );
      }

      lastIndex = match.index + full.length;
    }

    // Remaining text after last token
    if (lastIndex < text.length) {
      parts.push(
        <span key={`text-${lastIndex}`}>{text.slice(lastIndex)}</span>
      );
    }

    if (parts.length === 0) {
      return <p key={key} className="text-text-secondary">{text}</p>;
    }

    // If the paragraph is ONLY an image (no surrounding text), render without <p> wrapper
    const textContent = text.replace(/(!?\[[^\]]*\]\([^)]+\))/g, '').trim();
    if (hasImage && !textContent) {
      return <div key={key}>{parts}</div>;
    }

    return <p key={key} className="text-text-secondary">{parts}</p>;
  };

  return (
    <div className="min-h-screen bg-surface-primary font-condensed">
      {/* Header */}
      <NavBar
        activePage="news"
        hasTicker={false}
        onMobileMenuChange={setMobileNavOpen}
        rightSlot={
          <Link
            href="/news"
            className="flex items-center gap-1.5 text-text-secondary hover:text-text-primary transition-colors text-xs md:text-sm font-bold uppercase tracking-wide min-h-[44px] px-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="hidden sm:inline">Back to News</span>
          </Link>
        }
      />

      <main className="max-w-[1200px] mx-auto px-4 md:px-6 py-6 md:py-8">
        {loading ? (
          <div className="animate-pulse max-w-[860px]">
            <div className="h-8 bg-surface-tertiary rounded w-1/4 mb-4" />
            <div className="h-10 bg-surface-tertiary rounded w-3/4 mb-3" />
            <div className="h-5 bg-surface-tertiary rounded w-1/2 mb-6" />
            <div className="bg-surface-tertiary w-full rounded-[4px] mb-8" style={{ aspectRatio: '16/9' }} />
            <div className="space-y-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="h-4 bg-surface-tertiary rounded" style={{ width: `${85 + (i % 3) * 5}%` }} />
              ))}
            </div>
          </div>
        ) : notFound || !post ? (
          <div className="text-center py-24">
            <svg className="w-16 h-16 text-text-muted mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
            </svg>
            <p className="text-text-secondary text-xl mb-2">Post not found</p>
            <p className="text-text-muted text-sm mb-6">This article may have been removed or the link is incorrect.</p>
            <Link href="/news" className="inline-flex items-center gap-2 bg-brand-red text-white text-sm font-bold uppercase px-4 py-2 hover:bg-red-700 transition-colors">
              ← Back to News
            </Link>
          </div>
        ) : (
          /* Responsive layout: stacks on mobile, side-by-side on lg+ */
          <div className={`flex gap-6 lg:gap-8 items-start ${galleryImages.length > 0 ? 'flex-col lg:flex-row' : ''}`}>
            {/* ===== ARTICLE COLUMN ===== */}
            <article className={`w-full ${galleryImages.length > 0 ? 'lg:flex-1 lg:min-w-0' : 'max-w-[860px] mx-auto'}`}>
              {/* Category + Date */}
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="text-brand-red text-xs font-bold uppercase tracking-wide">{post.category || 'News'}</span>
                <span className="text-text-muted text-xs">·</span>
                <span className="text-text-muted text-xs">{formatDate(post.created_at)}</span>
                {post.views !== undefined && (
                  <>
                    <span className="text-text-muted text-xs">·</span>
                    <span className="text-text-muted text-xs">{post.views.toLocaleString()} views</span>
                  </>
                )}
                {isAdmin && post.id && (
                  <Link
                    href={`/?editPost=${post.id}`}
                    className="ml-auto flex items-center gap-1.5 bg-brand-red hover:bg-red-700 text-white text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-[4px] transition-colors"
                    title="Edit this post in Admin Panel"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Edit Post
                  </Link>
                )}
              </div>

              {/* Title */}
              <h1 className="text-text-primary text-2xl sm:text-3xl md:text-4xl font-bold leading-tight mb-3">
                {post.title}
              </h1>

              {/* Author + Share */}
              <div className="flex items-center justify-between gap-2 mb-5 pb-5 border-b border-stroke-primary flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-brand-red flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                    {(post.author || 'V')[0].toUpperCase()}
                  </div>
                  <span className="text-text-secondary text-sm">By <span className="text-text-primary font-semibold">{post.author || 'ViddStar'}</span></span>
                </div>

                {/* Share Button */}
                <div className="relative" ref={shareRef}>
                  <button
                    onClick={() => setShareOpen((prev) => !prev)}
                    className="flex items-center gap-1.5 text-text-secondary hover:text-text-primary bg-surface-tertiary hover:bg-surface-secondary border border-stroke-primary px-3 py-1.5 rounded-[4px] text-xs font-bold uppercase tracking-wide transition-colors"
                    aria-label="Share this article"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                    Share
                    <svg className={`w-3 h-3 transition-transform duration-200 ${shareOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Share Dropdown */}
                  {shareOpen && (
                    <div className="absolute right-0 top-full mt-2 w-52 bg-surface-secondary border border-stroke-primary rounded-[4px] shadow-2xl z-50 overflow-hidden">
                      <div className="px-3 py-2 border-b border-stroke-primary">
                        <p className="text-text-muted text-xs font-bold uppercase tracking-wide">Share this article</p>
                      </div>
                      <a href={shareLinks(post.title).facebook} target="_blank" rel="noopener noreferrer" onClick={() => setShareOpen(false)} className="flex items-center gap-3 px-3 py-2.5 hover:bg-surface-tertiary transition-colors group">
                        <div className="w-7 h-7 rounded-full bg-[#1877F2] flex items-center justify-center flex-shrink-0">
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                        </div>
                        <span className="text-text-secondary group-hover:text-text-primary text-sm font-medium">Facebook</span>
                      </a>
                      <a href={shareLinks(post.title).twitter} target="_blank" rel="noopener noreferrer" onClick={() => setShareOpen(false)} className="flex items-center gap-3 px-3 py-2.5 hover:bg-surface-tertiary transition-colors group">
                        <div className="w-7 h-7 rounded-full bg-black flex items-center justify-center flex-shrink-0">
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                        </div>
                        <span className="text-text-secondary group-hover:text-text-primary text-sm font-medium">X (Twitter)</span>
                      </a>
                      <a href={shareLinks(post.title).whatsapp} target="_blank" rel="noopener noreferrer" onClick={() => setShareOpen(false)} className="flex items-center gap-3 px-3 py-2.5 hover:bg-surface-tertiary transition-colors group">
                        <div className="w-7 h-7 rounded-full bg-[#25D366] flex items-center justify-center flex-shrink-0">
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                        </div>
                        <span className="text-text-secondary group-hover:text-text-primary text-sm font-medium">WhatsApp</span>
                      </a>
                      <button onClick={handleCopyLink} className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-surface-tertiary transition-colors group border-t border-stroke-primary">
                        <div className="w-7 h-7 rounded-full bg-surface-tertiary border border-stroke-primary flex items-center justify-center flex-shrink-0">
                          {copied ? (
                            <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                          ) : (
                            <svg className="w-4 h-4 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                          )}
                        </div>
                        <span className={`text-sm font-medium transition-colors ${copied ? 'text-green-400' : 'text-text-secondary group-hover:text-text-primary'}`}>
                          {copied ? 'Copied!' : 'Copy link'}
                        </span>
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Cover Image */}
              {post.cover_image && (
                <div className="mb-6 md:mb-8 w-full overflow-hidden rounded-[4px] aspect-video">
                  <img
                    src={post.cover_image}
                    alt={post.cover_image_alt || post.title}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/assets/images/no_image.png'; }}
                  />
                </div>
              )}

              {/* Ad — top of content */}
              {renderAd('top')}

              {/* Excerpt */}
              {post.excerpt && (
                <p className="text-text-secondary text-base md:text-lg leading-relaxed mb-5 md:mb-6 font-medium border-l-4 border-brand-red pl-4 italic">
                  {post.excerpt}
                </p>
              )}

              {/* Content */}
              {post.content ? (
                <div className="text-text-secondary text-sm md:text-base leading-relaxed space-y-4" style={{ lineHeight: '1.8' }}>
                  {post.content.split('\n').map((paragraph, i) => {
                    // Check if this is roughly the midpoint for mid-content ad
                    const lines = post.content!.split('\n').filter(l => l.trim());
                    const midPoint = Math.floor(lines.length / 2);
                    const nonEmptyIdx = post.content!.split('\n').filter((l, idx2) => idx2 <= i && l.trim()).length - 1;
                    const showMidAd = post.ad_placement === 'middle' && nonEmptyIdx === midPoint && paragraph.trim();

                    return (
                      <React.Fragment key={i}>
                        {paragraph.trim() ? (
                          renderParagraph(paragraph, i)
                        ) : (
                          <br />
                        )}
                        {showMidAd && (
                          <AdDisplay placement="blog-post" className="my-6" />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              ) : (
                <p className="text-text-muted text-base italic">No content available for this post.</p>
              )}

              {/* Ad — bottom of content (default) */}
              {(!post.ad_placement || post.ad_placement === 'bottom') && (
                <AdDisplay placement="blog-post" className="mt-8" />
              )}

              {/* Mobile Photo Gallery (shown below article on small screens) */}
              {galleryImages.length > 0 && (
                <div className="lg:hidden mt-8 border border-stroke-primary bg-surface-secondary rounded-[4px] overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-stroke-primary bg-surface-tertiary">
                    <span className="w-1 h-4 bg-brand-red inline-block rounded-sm flex-shrink-0" />
                    <span className="text-text-primary font-bold uppercase text-xs tracking-wide">Photo Gallery</span>
                    <span className="ml-auto text-text-muted text-xs font-normal">{galleryImages.length} photos</span>
                  </div>
                  <div className="p-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {galleryImages.map((imgUrl, idx) => (
                      <button
                        key={idx}
                        onClick={() => openLightbox(idx)}
                        className="relative overflow-hidden rounded-[2px] group focus:outline-none focus:ring-2 focus:ring-brand-red"
                        style={{ aspectRatio: '4/3' }}
                        aria-label={`View photo ${idx + 1}`}
                      >
                        <img
                          src={imgUrl}
                          alt={`${post.title} — photo ${idx + 1}`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/assets/images/no_image.png'; }}
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors duration-200" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="mt-8 md:mt-10 pt-5 md:pt-6 border-t border-stroke-primary flex items-center justify-between flex-wrap gap-3">
                <Link href="/news" className="inline-flex items-center gap-2 text-text-secondary hover:text-brand-red transition-colors text-sm font-bold uppercase tracking-wide">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back to News
                </Link>
                <div className="flex items-center gap-2">
                  <span className="text-text-muted text-xs mr-2">{formatDate(post.created_at)}</span>
                  <a href={shareLinks(post.title).facebook} target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook" className="w-7 h-7 rounded-full bg-[#1877F2] flex items-center justify-center hover:opacity-80 transition-opacity">
                    <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  </a>
                  <a href={shareLinks(post.title).twitter} target="_blank" rel="noopener noreferrer" aria-label="Share on X" className="w-7 h-7 rounded-full bg-black flex items-center justify-center hover:opacity-80 transition-opacity">
                    <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                  </a>
                  <a href={shareLinks(post.title).whatsapp} target="_blank" rel="noopener noreferrer" aria-label="Share on WhatsApp" className="w-7 h-7 rounded-full bg-[#25D366] flex items-center justify-center hover:opacity-80 transition-opacity">
                    <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  </a>
                </div>
              </div>

              {/* Comments Section */}
              {post.id && <NewsComments newsPostId={post.id} />}
            </article>

            {/* ===== PHOTO SIDEBAR (desktop only — hidden on mobile, shown inline above) ===== */}
            {galleryImages.length > 0 && (
              <aside className="hidden lg:block w-72 xl:w-80 flex-shrink-0">
                <div className="border border-stroke-primary bg-surface-secondary rounded-[4px] overflow-hidden sticky top-[86px]">
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-stroke-primary bg-surface-tertiary">
                    <span className="w-1 h-4 bg-brand-red inline-block rounded-sm flex-shrink-0" />
                    <span className="text-text-primary font-bold uppercase text-xs tracking-wide">Photo Gallery</span>
                    <span className="ml-auto text-text-muted text-xs font-normal">{galleryImages.length} photos</span>
                  </div>

                  {/* Featured / large first photo */}
                  <button
                    onClick={() => openLightbox(0)}
                    className="relative w-full overflow-hidden group focus:outline-none focus:ring-2 focus:ring-brand-red focus:ring-inset"
                    style={{ aspectRatio: '4/3' }}
                    aria-label="View photo 1"
                  >
                    <img
                      src={galleryImages[0]}
                      alt={`${post.title} — photo 1`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/assets/images/no_image.png'; }}
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors duration-200 flex items-center justify-center">
                      <svg className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                      </svg>
                    </div>
                    {galleryImages.length > 1 && (
                      <div className="absolute bottom-2 right-2">
                        <span className="bg-black/70 text-white text-xs font-bold px-2 py-0.5 rounded-[2px]">1 / {galleryImages.length}</span>
                      </div>
                    )}
                  </button>

                  {/* Thumbnail grid for remaining photos */}
                  {galleryImages.length > 1 && (
                    <div className="p-2 grid grid-cols-3 gap-1.5">
                      {galleryImages.slice(1).map((imgUrl, idx) => (
                        <button
                          key={idx + 1}
                          onClick={() => openLightbox(idx + 1)}
                          className="relative overflow-hidden rounded-[2px] group focus:outline-none focus:ring-2 focus:ring-brand-red"
                          style={{ aspectRatio: '1/1' }}
                          aria-label={`View photo ${idx + 2}`}
                        >
                          <img
                            src={imgUrl}
                            alt={`${post.title} — photo ${idx + 2}`}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/assets/images/no_image.png'; }}
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-200" />
                        </button>
                      ))}
                    </div>
                  )}

                  {galleryImages.length > 4 && (
                    <div className="px-4 pb-4">
                      <button
                        onClick={() => openLightbox(0)}
                        className="w-full text-center text-brand-red text-xs font-bold uppercase tracking-wide hover:underline py-1"
                      >
                        View all {galleryImages.length} photos
                      </button>
                    </div>
                  )}
                </div>
              </aside>
            )}
          </div>
        )}
      </main>

      {/* Related News */}
      {relatedPosts.length > 0 && !loading && !notFound && post && (
        <section className="max-w-[1200px] mx-auto px-4 md:px-6 pb-10">
          <div className="border-t border-stroke-primary pt-8">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-1 h-5 bg-brand-red inline-block rounded-sm flex-shrink-0" />
              <h2 className="text-text-primary font-bold uppercase text-sm tracking-widest">Related News</h2>
              {post.category && (
                <span className="text-text-muted text-xs font-normal ml-1">in {post.category}</span>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {relatedPosts.map((related) => (
                <Link
                  key={related.id}
                  href={`/news/${related.slug}`}
                  className="group flex flex-col bg-surface-secondary border border-stroke-primary rounded-[4px] overflow-hidden hover:border-brand-red transition-colors duration-200"
                >
                  {/* Thumbnail */}
                  <div className="relative w-full overflow-hidden bg-surface-tertiary" style={{ aspectRatio: '16/9' }}>
                    {related.cover_image ? (
                      <img
                        src={related.cover_image}
                        alt={related.cover_image_alt || related.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/assets/images/no_image.png'; }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                        </svg>
                      </div>
                    )}
                    {related.category && (
                      <span className="absolute top-2 left-2 bg-brand-red text-white text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-[2px]">
                        {related.category}
                      </span>
                    )}
                  </div>
                  {/* Text */}
                  <div className="flex flex-col flex-1 p-3 gap-1.5">
                    <h3 className="text-text-primary text-sm font-bold leading-snug line-clamp-2 group-hover:text-brand-red transition-colors">
                      {related.title}
                    </h3>
                    {related.excerpt && (
                      <p className="text-text-muted text-xs leading-relaxed line-clamp-2">{related.excerpt}</p>
                    )}
                    <div className="mt-auto flex items-center gap-2 pt-2">
                      <span className="text-text-muted text-[10px]">{formatDate(related.created_at)}</span>
                      {related.views !== undefined && (
                        <>
                          <span className="text-text-muted text-[10px]">·</span>
                          <span className="text-text-muted text-[10px]">{related.views.toLocaleString()} views</span>
                        </>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="border-t border-stroke-primary bg-surface-secondary mt-12">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6 flex flex-col md:flex-row items-center justify-between gap-2">
          <p className="text-text-muted text-xs">&copy; 2024 ViddStar. All rights reserved.</p>
          <Link href="/" className="text-text-muted hover:text-brand-red text-xs transition-colors">← Back to ViddStarTV</Link>
        </div>
      </footer>

      {/* Lightbox */}
      {lightboxImg && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxImg(null)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-brand-red transition-colors z-10"
            onClick={() => setLightboxImg(null)}
            aria-label="Close photo"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {galleryImages.length > 1 && (
            <button
              className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 text-white hover:text-brand-red transition-colors z-10 bg-black/40 rounded-full p-2"
              onClick={(e) => { e.stopPropagation(); lightboxPrev(); }}
              aria-label="Previous photo"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}

          <img
            src={lightboxImg}
            alt={`Gallery photo ${lightboxIndex + 1}`}
            className="max-w-full max-h-[80vh] md:max-h-[85vh] object-contain rounded-[4px] shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/assets/images/no_image.png'; }}
          />

          {galleryImages.length > 1 && (
            <button
              className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 text-white hover:text-brand-red transition-colors z-10 bg-black/40 rounded-full p-2"
              onClick={(e) => { e.stopPropagation(); lightboxNext(); }}
              aria-label="Next photo"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          {galleryImages.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
              <span className="bg-black/70 text-white text-xs font-bold px-3 py-1.5 rounded-full">
                {lightboxIndex + 1} / {galleryImages.length}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
